import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../../../components/feature/Layout";
import NudgeModal from "../../../components/feature/NudgeModal";
import { useNudgeModal } from "../../../hooks/useNudgeModal";
import { fetchMaterialBySlug } from "../../../repositories/materials";
import { fetchMaterialSiteLinks } from "../../../repositories/materialSiteLinks";
import type { Material } from "../../../types/materials";
import type { MaterialSiteLinkWithSite } from "../../../types/materialSiteLinks";
import { SUBJECT_ICON_MAP, getGradeRangeLabel } from "../../../constants/subjects";
import { useSEO, generateWebPageSchema } from "../../../utils/seo";

// URLから表示名を生成するヘルパー関数
function getDisplayNameFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace('www.', '');
    const pathname = urlObj.pathname.replace(/^\/|\/$/g, '');
    
    // ホスト名とパスを組み合わせて表示名を生成
    if (pathname && pathname.length > 0) {
      const pathParts = pathname.split('/').filter(Boolean);
      const lastPart = pathParts[pathParts.length - 1];
      return `${hostname} - ${lastPart}`;
    }
    
    return hostname;
  } catch {
    // URLのパースに失敗した場合はそのまま返す
    return url;
  }
}

export default function MaterialDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [material, setMaterial] = useState<Material | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [recommendedSites, setRecommendedSites] = useState<MaterialSiteLinkWithSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);

  // ナッジモーダルの制御
  const nudgeModal = useNudgeModal({
    delayMs: 5000,
    probability: 0.3,
  });

  // SEO設定（動的に更新）
  useSEO({
    title: material ? `${material.title}｜単元ガイド｜キョウコレ` : '単元ガイド｜キョウコレ',
    description: material?.description || '単元ガイドの詳細情報です。',
    keywords: material ? `${material.subject},${material.title},単元ガイド,教材,学習支援` : '単元ガイド,教材',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + `/materials/${slug}`,
    jsonLd: material ? generateWebPageSchema(
      material.title,
      material.description,
      `/materials/${slug}`
    ) : undefined,
  });

  useEffect(() => {
    // slug が空の場合は即座にエラー表示
    if (!slug || slug.trim() === '') {
      console.error('❌ slug が指定されていません');
      setError('単元ガイドのURLが正しくありません。');
      setNotFound(true);
      setLoading(false);
      return;
    }

    const loadMaterial = async () => {
      try {
        setLoading(true);
        setError(null);
        setNotFound(false);

        console.log('📖 単元ガイド詳細を取得中:', slug);
        const data = await fetchMaterialBySlug(slug);

        if (!data) {
          console.log('ℹ️ 単元ガイドが見つかりません:', slug);
          setNotFound(true);
        } else {
          console.log('✅ 単元ガイド詳細取得成功:', data.title);
          setMaterial(data);
          
          // おすすめサイトを取得
          loadRecommendedSites(data.id);
        }
      } catch (err: any) {
        console.error('❌ 単元ガイド詳細取得エラー:', err);
        setError(err.message || '単元ガイドの読み込みに失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    loadMaterial();
  }, [slug]);

  const loadRecommendedSites = async (materialId: string) => {
    try {
      setSitesLoading(true);
      console.log('🔗 おすすめサイトを取得中:', materialId);
      
      const sites = await fetchMaterialSiteLinks(materialId);
      
      // is_primary=true のみをフィルタリング
      const primarySites = sites.filter(link => link.isPrimary);
      
      console.log('✅ おすすめサイト取得成功:', primarySites.length, '件');
      setRecommendedSites(primarySites);
    } catch (err: any) {
      console.error('❌ おすすめサイト取得エラー:', err);
      // エラーが発生してもページ全体は落とさない
    } finally {
      setSitesLoading(false);
    }
  };

  // ローディング表示
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-gray-600">読み込み中…</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // 404表示
  if (notFound) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
              <i className="ri-file-search-line text-6xl text-gray-400 mb-4"></i>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">単元ガイドが見つかりません</h1>
              <p className="text-gray-600 mb-6">
                お探しの単元ガイドは存在しないか、削除された可能性があります。
              </p>
              <Link
                to="/materials"
                className="inline-flex items-center px-6 py-3 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                単元ガイド一覧に戻る
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // エラー表示
  if (error) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-start">
                <i className="ri-error-warning-line text-2xl text-red-600 mr-3 mt-1"></i>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-red-900 mb-2">エラーが発生しました</h2>
                  <p className="text-red-800 mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    再読み込み
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // 正常表示
  if (!material) {
    return null;
  }

  const icon = SUBJECT_ICON_MAP[material.subject];

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* パンくずリスト */}
          <nav className="mb-6 text-sm">
            <ol className="flex items-center space-x-2 text-gray-600">
              <li>
                <Link to="/" className="hover:text-teal-600 transition-colors">
                  ホーム
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li>
                <Link to="/materials" className="hover:text-teal-600 transition-colors">
                  単元ガイド一覧
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li className="text-gray-900 font-medium">{material.title}</li>
            </ol>
          </nav>

          {/* メインコンテンツ */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            {/* ヘッダー */}
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-8">
              <div className="flex items-start gap-4 mb-4">
                {icon && (
                  <div className={`flex h-12 w-12 items-center justify-center rounded-full text-2xl ${icon.bgClass} flex-shrink-0`}>
                    <span aria-hidden="true">{icon.emoji}</span>
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-sm font-semibold ${icon?.textClass || 'text-gray-700'}`}>
                      {material.subject}
                    </span>
                    <span className="text-sm text-gray-600">
                      {getGradeRangeLabel(material.grade_min, material.grade_max)}
                    </span>
                    {material.is_featured && (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                        <i className="ri-star-fill mr-1"></i>
                        注目
                      </span>
                    )}
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900">{material.title}</h1>
                </div>
              </div>

              {material.tags && material.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {material.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-full bg-white/80 px-3 py-1 text-sm font-medium text-gray-700 shadow-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* 本文 */}
            <div className="p-8">
              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-file-text-line mr-2 text-teal-600"></i>
                  単元の説明
                </h2>
                <div className="prose prose-slate max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {material.description}
                  </p>
                </div>
              </section>

              {material.recommended_points && (
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <i className="ri-lightbulb-line mr-2 text-teal-600"></i>
                    おすすめポイント
                  </h2>
                  <div className="bg-teal-50 rounded-lg p-6">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {material.recommended_points}
                    </p>
                  </div>
                </section>
              )}

              {/* おすすめ教材サイトセクション */}
              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-global-line mr-2 text-teal-600"></i>
                  おすすめ教材サイト
                </h2>
                {sitesLoading ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mb-2"></div>
                    <p className="text-gray-600 text-sm">読み込み中...</p>
                  </div>
                ) : recommendedSites.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {recommendedSites.slice(0, 12).map((link) => (
                      <a
                        key={link.id}
                        href={link.site.baseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group bg-white border border-gray-200 rounded-lg p-4 hover:border-teal-500 hover:shadow-md transition-all duration-200 cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <i className="ri-link text-teal-600 flex-shrink-0"></i>
                              <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-teal-600 transition-colors">
                                {link.site.name}
                              </h3>
                            </div>
                            {link.site.description && (
                              <p className="text-xs text-gray-600 line-clamp-2 mb-1">
                                {link.site.description}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 truncate">
                              {link.site.baseUrl}
                            </p>
                          </div>
                          <i className="ri-external-link-line text-gray-400 group-hover:text-teal-600 transition-colors flex-shrink-0"></i>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <i className="ri-links-line text-4xl text-gray-400 mb-3"></i>
                    <p className="text-gray-600">おすすめ教材サイトは準備中です</p>
                  </div>
                )}
              </section>

              {/* アクションボタン */}
              <div className="flex flex-wrap gap-4 pt-6 border-t border-gray-200">
                <Link
                  to={`/recipes/new?material=${material.id}`}
                  className="inline-flex items-center px-6 py-3 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  この単元のレシピを投稿
                </Link>
                <Link
                  to={`/recipes?material=${material.id}`}
                  className="inline-flex items-center px-6 py-3 bg-white text-teal-600 text-sm font-medium rounded-lg border-2 border-teal-600 hover:bg-teal-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-2-line mr-2"></i>
                  この単元のレシピを見る
                </Link>
              </div>
            </div>
          </div>

          {/* 戻るボタン */}
          <div className="mt-6">
            <Link
              to="/materials"
              className="inline-flex items-center text-gray-600 hover:text-teal-600 transition-colors"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              単元ガイド一覧に戻る
            </Link>
          </div>
        </div>
      </div>

      {/* ナッジモーダル */}
      <NudgeModal
        isOpen={nudgeModal.isOpen}
        onClose={nudgeModal.handleClose}
      />
    </Layout>
  );
}
